import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Tournament, Player, TournamentConfig, SkillLevel } from '../types';
import { generateSchedule } from '../services/api';

interface SetupPageProps {
  tournament: Tournament | null;
  onSave: (tournament: Tournament) => void;
  onUpdate: (updates: Partial<Tournament>) => void;
}

export default function SetupPage({ tournament, onSave }: SetupPageProps) {
  const navigate = useNavigate();
  const [name, setName] = useState(tournament?.config.name || '');
  const [format, setFormat] = useState<TournamentConfig['format']>(tournament?.config.format || 'rotating');
  const [courts, setCourts] = useState(tournament?.config.courts || 2);
  const [rounds, setRounds] = useState(tournament?.config.rounds || 5);
  const [targetScore, setTargetScore] = useState(tournament?.config.targetScore || 11);
  const [winBy, setWinBy] = useState(tournament?.config.winBy || 1);
  const [players, setPlayers] = useState<Player[]>(tournament?.players || []);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerSkill, setNewPlayerSkill] = useState<SkillLevel>(2);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const addPlayer = () => {
    if (!newPlayerName.trim()) return;
    
    const newPlayer: Player = {
      id: generateId(),
      name: newPlayerName.trim(),
      skill: newPlayerSkill,
      totalPoints: 0,
      wins: 0,
      timesSatOut: 0,
      partnerHistory: [],
      opponentHistory: []
    };
    
    setPlayers([...players, newPlayer]);
    setNewPlayerName('');
    // Keep the last selected skill level instead of resetting to default
  };

  const removePlayer = (id: string) => {
    setPlayers(players.filter(p => p.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (players.length < 4) {
      setError('At least 4 players are required');
      return;
    }

    if (!name.trim()) {
      setError('Tournament name is required');
      return;
    }

    setGenerating(true);

    try {
      const config: TournamentConfig = {
        name: name.trim(),
        format,
        courts,
        rounds,
        targetScore,
        winBy
      };

      const { schedule } = await generateSchedule(players, config);

      const newTournament: Tournament = {
        id: generateId(),
        config,
        players,
        schedule,
        isComplete: false,
        createdAt: new Date().toISOString()
      };

      onSave(newTournament);
      navigate('/schedule');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate schedule');
    } finally {
      setGenerating(false);
    }
  };

  const getSkillLabel = (skill: SkillLevel) => {
    switch (skill) {
      case 1: return 'Beginner';
      case 2: return 'Intermediate';
      case 3: return 'Advanced';
      default: return 'Unknown';
    }
  };

  const getSkillBadgeClass = (skill: SkillLevel) => {
    switch (skill) {
      case 1: return 'badge-beginner';
      case 2: return 'badge-intermediate';
      case 3: return 'badge-advanced';
      default: return '';
    }
  };

  return (
    <div>
      <h1>Tournament Setup</h1>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <h2>Tournament Configuration</h2>
          
          <div className="setup-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div>
              <label htmlFor="name">Tournament Name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter tournament name"
                required
              />
            </div>

            <div>
              <label htmlFor="format">Format</label>
              <select
                id="format"
                value={format}
                onChange={(e) => setFormat(e.target.value as TournamentConfig['format'])}
              >
                <option value="rotating">Rotating Partner Round Robin</option>
                <option value="balanced">Balanced (Strict Skill Matching)</option>
                <option value="swiss">Swiss Format</option>
                <option value="fixed-team">Fixed Team Round Robin</option>
              </select>
            </div>

            <div>
              <label htmlFor="courts">Number of Courts</label>
              <input
                id="courts"
                type="number"
                min="1"
                max="20"
                value={courts}
                onChange={(e) => setCourts(parseInt(e.target.value))}
              />
            </div>

            <div>
              <label htmlFor="rounds">Number of Rounds</label>
              <input
                id="rounds"
                type="number"
                min="1"
                max="50"
                value={rounds}
                onChange={(e) => setRounds(parseInt(e.target.value))}
              />
            </div>

            <div>
              <label htmlFor="targetScore">Target Score</label>
              <input
                id="targetScore"
                type="number"
                min="1"
                max="21"
                value={targetScore}
                onChange={(e) => setTargetScore(parseInt(e.target.value))}
              />
            </div>

            <div>
              <label htmlFor="winBy">Win By</label>
              <input
                id="winBy"
                type="number"
                min="1"
                max="3"
                value={winBy}
                onChange={(e) => setWinBy(parseInt(e.target.value))}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Players ({players.length})</h2>
          
          <div className="add-player-form flex gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
            <input
              type="text"
              value={newPlayerName}
              onChange={(e) => setNewPlayerName(e.target.value)}
              placeholder="Player name"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPlayer())}
              style={{ flex: 2, minWidth: '140px' }}
            />
            <select
              value={newPlayerSkill}
              onChange={(e) => setNewPlayerSkill(parseInt(e.target.value) as SkillLevel)}
              style={{ flex: 1, minWidth: '110px', width: 'auto' }}
            >
              <option value={1}>Beginner</option>
              <option value={2}>Intermediate</option>
              <option value={3}>Advanced</option>
            </select>
            <button type="button" className="btn-primary add-player-btn" onClick={addPlayer}>
              Add Player
            </button>
          </div>

          {players.length === 0 ? (
            <p className="text-center">No players added yet. Add at least 4 players to start.</p>
          ) : (
            <div className="players-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
              {players.map((player) => (
                <div key={player.id} className="card player-card" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  gap: '0.75rem'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: '1rem' }}>{player.name}</strong>
                    <span className={`badge ${getSkillBadgeClass(player.skill)}`} style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>
                      {getSkillLabel(player.skill)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn-danger remove-player-btn"
                    onClick={() => removePlayer(player.id)}
                    style={{ 
                      padding: '0.35rem 0.6rem', 
                      fontSize: '0.875rem',
                      minWidth: 'auto',
                      width: 'auto',
                      flexShrink: 0,
                      lineHeight: 1
                    }}
                    title="Remove player"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          type="submit"
          className="btn-success"
          disabled={generating || players.length < 4}
          style={{ width: '100%', fontSize: '1.25rem', padding: '1rem' }}
        >
          {generating ? 'Generating...' : '🎾 Generate Tournament Schedule'}
        </button>
      </form>
    </div>
  );
}